import React, { useState, useEffect, useRef } from 'react';
import { MessageCircle, X, Send, Minimize2, Maximize2, Loader2, AlertCircle } from 'lucide-react';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Card, CardContent, CardHeader, CardTitle } from './ui/card';
import { useToast } from '../hooks/use-toast';
import { useAuth } from '../context/AuthContext';
import { useLanguage } from '../context/LanguageContext';

const ChatbotWidget = () => {
  const [isOpen, setIsOpen] = useState(false);
  const [isMinimized, setIsMinimized] = useState(false);
  const [messages, setMessages] = useState([]);
  const [inputMessage, setInputMessage] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const [sessionId, setSessionId] = useState('');
  const [showTicketForm, setShowTicketForm] = useState(false);
  const [ticketData, setTicketData] = useState({
    subject: '',
    message: '',
    category: 'technical'
  });
  
  const messagesEndRef = useRef(null);
  const { toast } = useToast();
  const { user } = useAuth();
  const { language, t } = useLanguage();
  const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

  // Generate session ID on mount
  useEffect(() => {
    const id = `session-${Date.now()}-${Math.random().toString(36).substring(7)}`;
    setSessionId(id);
  }, []);

  // Auto-scroll to bottom
  useEffect(() => {
    scrollToBottom();
  }, [messages]);

  const scrollToBottom = () => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  };

  const handleSendMessage = async () => {
    if (!inputMessage.trim() || isLoading) return;

    const userMessage = inputMessage.trim();
    setInputMessage('');
    
    // Add user message to chat
    setMessages(prev => [...prev, {
      role: 'user',
      content: userMessage,
      timestamp: new Date().toISOString()
    }]);

    setIsLoading(true);

    try {
      const response = await fetch(`${BACKEND_URL}/api/chatbot/chat`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({
          session_id: sessionId,
          message: userMessage,
          language: language,
          user_id: user?.id || null
        })
      });

      if (response.ok) {
        const data = await response.json();
        
        // Add bot response to chat
        setMessages(prev => [...prev, {
          role: 'assistant',
          content: data.response,
          timestamp: new Date().toISOString()
        }]);

        // Check if escalation is needed
        if (data.requires_escalation) {
          setTimeout(() => {
            setShowTicketForm(true);
          }, 1000);
        }
      } else {
        throw new Error('Failed to get response');
      }
    } catch (error) {
      console.error('Chat error:', error);
      toast({
        title: t('error') || 'Error',
        description: t('chatErrorMessage') || 'Failed to send message. Please try again.',
        variant: 'destructive'
      });
      
      // Add error message to chat
      setMessages(prev => [...prev, {
        role: 'assistant',
        content: language === 'ar' 
          ? 'عذراً، حدث خطأ. يرجى المحاولة مرة أخرى.' 
          : 'Sorry, an error occurred. Please try again.',
        timestamp: new Date().toISOString(),
        isError: true
      }]);
    } finally {
      setIsLoading(false);
    }
  };

  const handleKeyPress = (e) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      handleSendMessage();
    }
  };

  const handleCreateTicket = async () => {
    if (!ticketData.subject || !ticketData.message) {
      toast({
        title: language === 'ar' ? 'خطأ' : 'Error',
        description: language === 'ar' 
          ? 'يرجى ملء جميع الحقول المطلوبة' 
          : 'Please fill in all required fields',
        variant: 'destructive'
      });
      return;
    }

    try {
      const response = await fetch(`${BACKEND_URL}/api/chatbot/support-ticket`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          ...(user && { 'Authorization': `Bearer ${user.email}` })
        },
        body: JSON.stringify({
          ...ticketData,
          user_email: user?.email || null,
          user_name: user?.name || null,
          user_id: user?.id || null
        })
      });

      if (response.ok) {
        const data = await response.json();
        toast({
          title: language === 'ar' ? 'نجاح' : 'Success',
          description: language === 'ar'
            ? `تم إنشاء تذكرة الدعم بنجاح. رقم التذكرة: ${data.ticket_id.substring(0, 8)}`
            : `Support ticket created successfully. Ticket ID: ${data.ticket_id.substring(0, 8)}`
        });
        
        // Add confirmation message to chat
        setMessages(prev => [...prev, {
          role: 'assistant',
          content: language === 'ar'
            ? `تم إنشاء تذكرة الدعم الخاصة بك. سيتواصل معك فريق الدعم في غضون 24-48 ساعة.`
            : `Your support ticket has been created. Our team will reach out to you within 24-48 hours.`,
          timestamp: new Date().toISOString()
        }]);
        
        setShowTicketForm(false);
        setTicketData({ subject: '', message: '', category: 'technical' });
      } else {
        throw new Error('Failed to create ticket');
      }
    } catch (error) {
      console.error('Ticket creation error:', error);
      toast({
        title: language === 'ar' ? 'خطأ' : 'Error',
        description: language === 'ar'
          ? 'فشل إنشاء تذكرة الدعم'
          : 'Failed to create support ticket',
        variant: 'destructive'
      });
    }
  };

  const toggleOpen = () => {
    setIsOpen(!isOpen);
    setIsMinimized(false);
    
    // Load initial greeting if first time opening
    if (!isOpen && messages.length === 0) {
      const greeting = language === 'ar'
        ? 'مرحباً! أنا مساعد الدعم الفني لمنصة RC. كيف يمكنني مساعدتك اليوم؟'
        : 'Hello! I\'m your RC Platform support assistant. How can I help you today?';
      
      setMessages([{
        role: 'assistant',
        content: greeting,
        timestamp: new Date().toISOString()
      }]);
    }
  };

  if (!isOpen) {
    return (
      <div className="fixed bottom-6 right-6 z-50">
        <Button
          onClick={toggleOpen}
          className="h-14 w-14 rounded-full shadow-lg bg-cyan-600 hover:bg-cyan-700 flex items-center justify-center"
          title={language === 'ar' ? 'افتح الدعم' : 'Open Support Chat'}
        >
          <MessageCircle className="h-6 w-6 text-white" />
        </Button>
      </div>
    );
  }

  return (
    <div className={`fixed bottom-6 right-6 z-50 ${isMinimized ? 'w-80' : 'w-96'}`}>
      <Card className="shadow-2xl border-2 border-cyan-500">
        <CardHeader className="bg-cyan-600 text-white p-4 flex flex-row items-center justify-between">
          <div className="flex items-center gap-2">
            <MessageCircle className="h-5 w-5" />
            <CardTitle className="text-lg font-semibold">
              {language === 'ar' ? 'دعم RC Platform' : 'RC Platform Support'}
            </CardTitle>
          </div>
          <div className="flex gap-2">
            <Button
              variant="ghost"
              size="sm"
              onClick={() => setIsMinimized(!isMinimized)}
              className="h-8 w-8 p-0 text-white hover:bg-cyan-700"
            >
              {isMinimized ? <Maximize2 className="h-4 w-4" /> : <Minimize2 className="h-4 w-4" />}
            </Button>
            <Button
              variant="ghost"
              size="sm"
              onClick={() => setIsOpen(false)}
              className="h-8 w-8 p-0 text-white hover:bg-cyan-700"
            >
              <X className="h-4 w-4" />
            </Button>
          </div>
        </CardHeader>

        {!isMinimized && (
          <CardContent className="p-0">
            {/* Support Ticket Form */}
            {showTicketForm ? (
              <div className="p-4 space-y-4 max-h-96 overflow-y-auto">
                <div className="flex items-center justify-between mb-4">
                  <h3 className="font-semibold">
                    {language === 'ar' ? 'إنشاء تذكرة دعم' : 'Create Support Ticket'}
                  </h3>
                  <Button
                    variant="ghost"
                    size="sm"
                    onClick={() => setShowTicketForm(false)}
                  >
                    <X className="h-4 w-4" />
                  </Button>
                </div>
                
                <div className="space-y-3">
                  <div>
                    <label className="text-sm font-medium">
                      {language === 'ar' ? 'الموضوع' : 'Subject'} *
                    </label>
                    <Input
                      value={ticketData.subject}
                      onChange={(e) => setTicketData({...ticketData, subject: e.target.value})}
                      placeholder={language === 'ar' ? 'وصف مختصر للمشكلة' : 'Brief description of the issue'}
                    />
                  </div>
                  
                  <div>
                    <label className="text-sm font-medium">
                      {language === 'ar' ? 'التصنيف' : 'Category'} *
                    </label>
                    <select
                      value={ticketData.category}
                      onChange={(e) => setTicketData({...ticketData, category: e.target.value})}
                      className="w-full p-2 border rounded-md"
                    >
                      <option value="technical">{language === 'ar' ? 'مشكلة تقنية' : 'Technical Issue'}</option>
                      <option value="account">{language === 'ar' ? 'مشكلة في الحساب' : 'Account Issue'}</option>
                      <option value="billing">{language === 'ar' ? 'الفوترة' : 'Billing'}</option>
                      <option value="feature">{language === 'ar' ? 'طلب ميزة' : 'Feature Request'}</option>
                      <option value="other">{language === 'ar' ? 'أخرى' : 'Other'}</option>
                    </select>
                  </div>
                  
                  <div>
                    <label className="text-sm font-medium">
                      {language === 'ar' ? 'الرسالة' : 'Message'} *
                    </label>
                    <textarea
                      value={ticketData.message}
                      onChange={(e) => setTicketData({...ticketData, message: e.target.value})}
                      placeholder={language === 'ar' ? 'تفاصيل المشكلة' : 'Details of your issue'}
                      className="w-full p-2 border rounded-md min-h-[100px]"
                    />
                  </div>
                  
                  <Button
                    onClick={handleCreateTicket}
                    className="w-full bg-cyan-600 hover:bg-cyan-700"
                  >
                    {language === 'ar' ? 'إنشاء تذكرة' : 'Create Ticket'}
                  </Button>
                </div>
              </div>
            ) : (
              <>
                {/* Chat Messages */}
                <div className="h-96 overflow-y-auto p-4 space-y-4 bg-gray-50">
                  {messages.map((msg, index) => (
                    <div
                      key={index}
                      className={`flex ${msg.role === 'user' ? 'justify-end' : 'justify-start'}`}
                    >
                      <div
                        className={`max-w-[80%] p-3 rounded-lg ${
                          msg.role === 'user'
                            ? 'bg-cyan-600 text-white'
                            : msg.isError
                            ? 'bg-red-100 text-red-800 border border-red-300'
                            : 'bg-white text-gray-800 border border-gray-200'
                        }`}
                      >
                        {msg.isError && (
                          <AlertCircle className="h-4 w-4 inline mr-2" />
                        )}
                        <p className="text-sm whitespace-pre-wrap">{msg.content}</p>
                        <span className="text-xs opacity-70 mt-1 block">
                          {new Date(msg.timestamp).toLocaleTimeString(language === 'ar' ? 'ar-SA' : 'en-US', {
                            hour: '2-digit',
                            minute: '2-digit'
                          })}
                        </span>
                      </div>
                    </div>
                  ))}
                  
                  {isLoading && (
                    <div className="flex justify-start">
                      <div className="bg-white p-3 rounded-lg border border-gray-200">
                        <Loader2 className="h-5 w-5 animate-spin text-cyan-600" />
                      </div>
                    </div>
                  )}
                  
                  <div ref={messagesEndRef} />
                </div>

                {/* Input Area */}
                <div className="p-4 border-t bg-white">
                  <div className="flex gap-2">
                    <Input
                      value={inputMessage}
                      onChange={(e) => setInputMessage(e.target.value)}
                      onKeyPress={handleKeyPress}
                      placeholder={language === 'ar' ? 'اكتب رسالتك...' : 'Type your message...'}
                      disabled={isLoading}
                      className={language === 'ar' ? 'text-right' : ''}
                    />
                    <Button
                      onClick={handleSendMessage}
                      disabled={isLoading || !inputMessage.trim()}
                      className="bg-cyan-600 hover:bg-cyan-700"
                    >
                      <Send className="h-4 w-4" />
                    </Button>
                  </div>
                  
                  <div className="mt-2 text-center">
                    <button
                      onClick={() => setShowTicketForm(true)}
                      className="text-xs text-cyan-600 hover:underline"
                    >
                      {language === 'ar' ? 'بحاجة إلى مساعدة بشرية؟ إنشاء تذكرة دعم' : 'Need human help? Create a support ticket'}
                    </button>
                  </div>
                </div>
              </>
            )}
          </CardContent>
        )}
      </Card>
    </div>
  );
};

export default ChatbotWidget;
